import { create } from 'zustand';
import { persist, createJSONStorage, type StateStorage } from 'zustand/middleware';
import type { AuthenticatedUserDto, LoginResponse } from '@bhops/shared';

interface AuthState {
  user: AuthenticatedUserDto | null;
  accessToken: string | null;
  refreshToken: string | null;
  setSession: (session: LoginResponse) => void;
  clear: () => void;
}

export const AUTH_STORAGE_KEY = 'bhops-auth';

// Persistence note — 갱신 토큰(과 사용자)은 **localStorage**, 접근 토큰은 메모리에만 둔다.
//
// 2026-10-01 까지는 sessionStorage(탭마다 따로)였다. 그래서 새 탭(구글 캘린더의 콘솔 링크, 주소
// 직접 입력, 즐겨찾기)을 열 때마다 다시 로그인해야 했다(오너 보고). 이제 브라우저 하나에 로그인
// 하나 — 모든 탭이 같은 갱신 토큰을 쓴다. 로그인은 갱신 토큰 수명(서버 JWT_REFRESH_TTL, 30일,
// 쓸 때마다 연장) 동안 브라우저를 닫았다 열어도 남고, 로그아웃은 모든 탭에서 함께 된다.
//
// ⚠ 같은 토큰을 여러 탭이 쓰므로 **갱신은 한 번에 한 탭만**, 그리고 늘 저장소의 **최신** 토큰으로
// 한다(api/client.ts 의 refreshTokens). 서버가 갱신 때마다 토큰을 바꾸고, 이미 바뀐 옛 토큰이 오면
// 그 갈래 전체를 끊기(재사용 감지) 때문이다.
//
// Tauri-specific follow-up (§D-1 POC): replace localStorage with the secure
// OS keychain via tauri-plugin-stronghold so RT isn't readable from a hostile
// browser extension if the app is ever run outside the Tauri shell.

/**
 * localStorage 저장소 — 예전 판(sessionStorage)에 로그인해 둔 탭을 새로고침하면 그 로그인을 한 번
 * 옮겨 온다(배포 직후 다시 로그인하지 않게).
 */
const sharedStorage: StateStorage = {
  getItem: (name) => {
    const v = localStorage.getItem(name);
    if (v !== null) return v;
    const legacy = sessionStorage.getItem(name);
    if (legacy !== null) {
      localStorage.setItem(name, legacy);
      sessionStorage.removeItem(name);
    }
    return legacy;
  },
  setItem: (name, value) => {
    localStorage.setItem(name, value);
  },
  removeItem: (name) => {
    localStorage.removeItem(name);
    sessionStorage.removeItem(name);
  },
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      setSession: (session) =>
        set({
          user: session.user,
          accessToken: session.accessToken,
          refreshToken: session.refreshToken,
        }),
      clear: () => set({ user: null, accessToken: null, refreshToken: null }),
    }),
    {
      name: AUTH_STORAGE_KEY,
      storage: createJSONStorage(() => sharedStorage),
      partialize: (state) => ({
        user: state.user,
        refreshToken: state.refreshToken,
      }),
    },
  ),
);

/**
 * 다른 탭이 저장소를 바꾸면(갱신·로그인·로그아웃) 이 탭의 메모리도 맞춘다. 로그아웃이면 이 탭의
 * 접근 토큰도 버려 함께 로그아웃된다. 다른 사람으로 다시 로그인했으면 이 탭의 접근 토큰(옛 사람)도
 * 버린다 — 다음 요청이 새 사람의 갱신 토큰으로 접근 토큰을 다시 받는다.
 */
export async function syncAuthFromStorage(): Promise<void> {
  const before = useAuthStore.getState().user?.id ?? null;
  await useAuthStore.persist.rehydrate();
  const s = useAuthStore.getState();
  if (!s.refreshToken || (s.user?.id ?? null) !== before) {
    useAuthStore.setState({ accessToken: null });
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === AUTH_STORAGE_KEY || e.key === null) void syncAuthFromStorage();
  });
}
